import { Container, Title, Text, Stack, Badge, Group, Button } from '@mantine/core';
import { Link, useLoaderData, type LoaderFunctionArgs } from 'react-router';
import { getPostBySlug, type BlogPost } from '../blog/posts';
import { useTheme } from '../theme/theme';
import { useSfyr } from '../_ctx/sfyr';

export async function blogPostPageLoader({ params }: LoaderFunctionArgs) {
  const lang = params.lang === 'it' ? 'it' : 'en';
  const post = await getPostBySlug({ slug: params.slug!, lang });
  if (!post) {
    throw new Response('Not Found', { status: 404 });
  }
  return post;
}

export const BlogPostPage = () => {
  const post = useLoaderData() as BlogPost;
  const theme = useTheme();
  const sfyr = useSfyr();

  return (
    <Container size='md' py='xl'>
      <Stack gap='lg'>
        <Group justify='space-between' align='flex-start' mb='lg'>
          <div style={{ flex: 1 }}>
            <Title order={1} mb='xs'>
              {post.title}
            </Title>
            <Group gap='xs'>
              <Badge variant='light'>{post.author}</Badge>
              <Text size='sm' c={theme.v0.col.textDimmed.hex}>
                {new Date(post.date).toLocaleDateString(post.lang === 'it' ? 'it-IT' : 'en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </Text>
            </Group>
          </div>
        </Group>

        <div
          dangerouslySetInnerHTML={{
            __html: post.content
              .split('\n')
              .map((line) => {
                const imageMatch = line.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
                if (imageMatch) {
                  return `<img src="${imageMatch[2]}" alt="${
                    imageMatch[1]
                  }" style="width: 100%; border-radius: 12px; margin: 16px 0;" />`;
                }
                if (line.startsWith('# ')) {
                  return `<h1 style="margin-top: 24px; margin-bottom: 16px; font-size: 28px; font-weight: 600;">${
                    line.slice(2)
                  }</h1>`;
                }
                if (line.startsWith('## ')) {
                  return `<h2 style="margin-top: 20px; margin-bottom: 12px; font-size: 24px; font-weight: 600;">${
                    line.slice(3)
                  }</h2>`;
                }
                if (line.startsWith('### ')) {
                  return `<h3 style="margin-top: 16px; margin-bottom: 8px; font-size: 20px; font-weight: 600;">${
                    line.slice(4)
                  }</h3>`;
                }
                if (line.startsWith('- ')) {
                  return `<li style="margin-left: 24px; margin-bottom: 8px;">${
                    line.slice(2).replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
                  }</li>`;
                }
                if (line.startsWith('* ')) {
                  return `<li style="margin-left: 24px; margin-bottom: 8px;">${
                    line.slice(2).replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
                  }</li>`;
                }
                if (line.trim() === '') {
                  return '<div style="height: 12px;"></div>';
                }
                return `<p style="margin-bottom: 12px; line-height: 1.6;">${
                  line.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
                }</p>`;
              })
              .join(''),
          }}
          style={{ lineHeight: 1.8, color: theme.v0.col.text.hex }}
        />

        <div style={{ borderTop: `1px solid ${theme.v0.col.backgroundTertiary.hex}`, paddingTop: 24, marginTop: 24 }}>
          <Link to='/blog'>
            <Button variant='light'>
              {sfyr('landing.{lang}.blog.back', {
                defaults: {
                  'landing.{lang=en}.blog.back': 'Back to Blog',
                  'landing.{lang=it}.blog.back': 'Torna al Blog',
                },
              })}
            </Button>
          </Link>
        </div>
      </Stack>
    </Container>
  );
};
